import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DirectDebtPaymentMethodChangeFormData,
  EditAccountFormValues,
  editAccountSchema,
} from "src/schemas/DirectDebtFormSchema";
import { CaptionS } from "src/components/ui/typographic";
import {
  AGENCY_MAX_LENGTH,
  ACCOUNT_MAX_LENGTH,
  DIGIT_MAX_LENGTH,
} from "src/schemas/DirectDebtFormSchema";
import { CONSTANTS_STRINGS } from "src/constants";
import { useValidateAccountData } from "src/hooks/api";
import { toast } from "sonner";
import { debugLog } from "src/utils";
import { useParams } from "react-router";
import { Loader2 } from "lucide-react";

interface EditAccountFormProps {
  accountData: DirectDebtPaymentMethodChangeFormData;
  onCancel: () => void;
  onSave: (updatedData: DirectDebtPaymentMethodChangeFormData) => void;
}

const EditAccountForm: React.FC<EditAccountFormProps> = ({
  accountData,
  onCancel,
  onSave,
}) => {
  const { token } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditAccountFormValues>({
    resolver: zodResolver(editAccountSchema),
    defaultValues: {
      agency: accountData.agency,
      account: accountData.account,
      digit: accountData.digit,
    },
  });

  const { mutate: validateAccountData, isPending } = useValidateAccountData();

  const onSubmit = (editAccountData: EditAccountFormValues) => {
    if (!token) {
      return;
    }
    validateAccountData(
      {
        token,
        request: {
          bankNumber: accountData?.bankData?.number,
          agency: editAccountData.agency,
          account: editAccountData.account + editAccountData.digit,
        },
      },
      {
        onSuccess: (data) => {
          if (!data.valid) {
            toast.error(data.message);
            return;
          }
          onSave({
            ...accountData,
            agency: editAccountData.agency,
            account: editAccountData.account,
            digit: editAccountData.digit,
          });
        },
        onError: (error) => {
          debugLog(error);
          toast.error(error.cause);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <fieldset>
        <legend className="sr-only">Dados da conta bancária</legend>

        <div className="space-y-4">
          <div className="form-field">
            <label htmlFor="agency" className="block">
              <CaptionS>{CONSTANTS_STRINGS.DIRECT_DEBIT_AGENCY}</CaptionS>
            </label>
            <input
              id="agency"
              type="text"
              inputMode="numeric"
              maxLength={AGENCY_MAX_LENGTH}
              {...register("agency")}
              aria-invalid={errors.agency ? "true" : "false"}
              aria-describedby={errors.agency ? "agency-error" : undefined}
              className="ease caption-m-low border-brand-blue-2 w-full rounded-[4px] border bg-transparent p-2 transition duration-300 hover:border-slate-400 focus:border-slate-700 focus:shadow focus:outline-none"
              placeholder="Digite a agência"
            />
            <span className="text-xs text-gray-500">
              Se a agência tiver 3 dígitos, adicione um 0 à esquerda
            </span>
            {errors.agency && (
              <div
                id="agency-error"
                className="text-primary-1 self-stretch text-sm leading-[21px] font-normal"
                role="alert"
              >
                {errors.agency.message}
              </div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="account" className="block">
              <CaptionS>{CONSTANTS_STRINGS.DIRECT_DEBIT_ACCOUNT}</CaptionS>
            </label>
            <input
              id="account"
              type="text"
              inputMode="numeric"
              maxLength={ACCOUNT_MAX_LENGTH}
              {...register("account")}
              aria-invalid={errors.account ? "true" : "false"}
              aria-describedby={errors.account ? "account-error" : undefined}
              className="ease caption-m-low border-brand-blue-2 w-full rounded-[4px] border bg-transparent p-2 transition duration-300 hover:border-slate-400 focus:border-slate-700 focus:shadow focus:outline-none"
              placeholder="Digite a conta"
            />
            {errors.account && (
              <div
                id="account-error"
                className="text-primary-1 self-stretch text-sm leading-[21px] font-normal"
                role="alert"
              >
                {errors.account.message}
              </div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="digit" className="block">
              <CaptionS>{CONSTANTS_STRINGS.DIRECT_DEBIT_DIGIT}</CaptionS>
            </label>
            <input
              id="digit"
              type="text"
              inputMode="text"
              maxLength={DIGIT_MAX_LENGTH}
              {...register("digit")}
              aria-invalid={errors.digit ? "true" : "false"}
              aria-describedby={errors.digit ? "digit-error" : undefined}
              className="ease caption-m-low border-brand-blue-2 w-full rounded-[4px] border bg-transparent p-2 transition duration-300 hover:border-slate-400 focus:border-slate-700 focus:shadow focus:outline-none"
              placeholder="Digite o dígito"
            />
            {errors.digit && (
              <div
                id="digit-error"
                className="text-primary-1 self-stretch text-sm leading-[21px] font-normal"
                role="alert"
              >
                {errors.digit.message}
              </div>
            )}
          </div>
        </div>
      </fieldset>

      <footer className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="bg-primary-1 flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white"
          disabled={isPending}
        >
          {isPending && <Loader2 className="animate-spin" />}
          Salvar
        </button>
      </footer>
    </form>
  );
};

export default EditAccountForm;
